import React, { useState, useEffect } from 'react';
import BudgetChart from '../components/BudgetChart';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, getDocs, collection, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// ── Helpers ──────────────────────────────────────────────────────────────────

function generateMonths(start, end) {
  if (!start || !end) return [];
  const months = [];
  let [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  while ((sy < ey || (sy === ey && sm <= em)) && months.length < 36) {
    months.push(`${sy}-${String(sm).padStart(2, '0')}`);
    sm++;
    if (sm > 12) { sm = 1; sy++; }
  }
  return months;
}

const MO = ['januari','februari','mars','april','maj','juni',
            'juli','augusti','september','oktober','november','december'];
const MO_SH = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];

function fmtMonthLong(ym) {
  const [y, m] = ym.split('-');
  return MO[parseInt(m, 10) - 1] + ' ' + y;
}

function fmtMonthShort(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return MO_SH[parseInt(m, 10) - 1] + ' ' + y;
}

function n(v) { return Math.round(parseFloat(v) || 0); }
function sep(v) { return n(v) === 0 ? '–' : n(v).toLocaleString('sv-SE'); }

function licTotal(lic) {
  const p = parseFloat(lic.price) || 0;
  if (lic.priceType === 'fixed') return p;
  return p * (parseFloat(lic.quantity) || 1);
}

const LIC_LABEL = { fixed: 'Fast pris', monthly: 'Per månad', quarterly: 'Per kvartal', yearly: 'Per år' };

// ── Static layout primitive ───────────────────────────────────────────────────

const SectionTable = ({ children, mb = 14 }) => (
  <table width="100%" cellSpacing={0} cellPadding={0}
    style={{ borderCollapse: 'collapse', marginBottom: mb }}>
    <tbody>{children}</tbody>
  </table>
);

// ── Main component ───────────────────────────────────────────────────────────

export default function BudgetReport() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const budgetId = searchParams.get('budgetId');
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [project, setProject]     = useState(null);
  const [budget, setBudget]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [foretag, setForetag]     = useState('');
  const [skapadAv, setSkapadAv]   = useState('');
  const [budgetnamn, setBudgetnamn] = useState('');
  const [datum, setDatum]         = useState('');
  const [revision, setRevision]   = useState('A');
  const [logoUrl, setLogoUrl]     = useState('/tikab-logo.svg');
  const [sizeMode, setSizeMode]   = useState('M');

  useEffect(() => {
    // Only set default if not already overridden by a budget-saved value
    setSkapadAv(prev => prev || userProfile?.name || currentUser?.displayName || currentUser?.email || '');
  }, [userProfile, currentUser]);

  useEffect(() => {
    if (!budget) return;
    // Use saved budgetnamn, fall back to budget tab name
    setBudgetnamn(budget.budgetnamn ?? budget.name ?? '');
    if (budget.foretag)   setForetag(budget.foretag);
    if (budget.skapadAv)  setSkapadAv(budget.skapadAv);
    if (budget.revision)  setRevision(budget.revision);
    if (budget.datum) {
      setDatum(budget.datum);
    } else {
      const today = new Date();
      const y = today.getFullYear();
      const mo = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      setDatum(`${y}-${mo}-${d}`);
    }
  }, [budget]);

  async function saveBudgetMeta(field, value) {
    if (!budget?.id) return;
    try {
      const snap = await getDoc(doc(db, 'projects', projectId));
      if (!snap.exists()) return;
      const budgets = (snap.data().budgets || []).map(b =>
        b.id === budget.id ? { ...b, [field]: value } : b
      );
      await updateDoc(doc(db, 'projects', projectId), { budgets });
    } catch (_) {}
  }

  useEffect(() => {
    async function loadLogo() {
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const companyId = userSnap.data()?.companyId;
        let orgData = null;
        if (companyId) {
          const orgSnap = await getDoc(doc(db, 'companies', companyId));
          if (orgSnap.exists()) orgData = orgSnap.data();
        }
        if (!orgData) {
          const all = await getDocs(collection(db, 'companies'));
          if (!all.empty) orgData = all.docs[0].data();
        }
        if (orgData?.logo) setLogoUrl(orgData.logo);
        // Only set company default if budget hasn't saved a specific value
        if (orgData?.name) setForetag(prev => prev || orgData.name);
      } catch (_) {}
    }
    if (currentUser?.uid) loadLogo();
  }, [currentUser]);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'projects', projectId));
        if (!snap.exists()) { setLoading(false); return; }
        const data = snap.data();
        setProject({ id: snap.id, ...data });
        const budgets = data.budgets || [];
        const found = budgetId ? budgets.find(b => b.id === budgetId) : budgets[0];
        if (found) {
          found.activities = (found.activities || []).map(a => {
            if (a.resourceLines) return a;
            return { ...a, resourceLines: [{ id: a.id + '_l', resourceId: a.resourceId || '', months: a.months || {} }] };
          });
        }
        setBudget(found || null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [projectId, budgetId]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
      <CircularProgress />
    </Box>
  );
  if (!project || !budget) return (
    <Box sx={{ p: 4 }}>
      Ingen budget hittades.{' '}
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Tillbaka</Button>
    </Box>
  );

  // ── Font sizes based on sizeMode ────────────────────────────────────────────
  const BASE_PT = { S: 7.5, M: 9, L: 10.5 };
  const basePt = BASE_PT[sizeMode];

  // TH/TD defined here to close over basePt
  const TH = ({ children, align = 'left', w }) => (
    <th style={{
      textAlign: align,
      fontWeight: 700,
      fontSize: '0.86em',
      padding: `0 6pt 3pt 0`,
      borderBottom: '0.75pt solid #000',
      width: w,
      whiteSpace: 'nowrap',
    }}>{children}</th>
  );

  const TD = ({ children, align = 'left', bold, indent, color }) => (
    <td style={{
      textAlign: align,
      fontSize: '1em',
      padding: `2pt 6pt 2pt ${indent ? 10 : 0}pt`,
      fontWeight: bold ? 700 : 400,
      color: color || 'inherit',
      whiteSpace: 'nowrap',
    }}>{children}</td>
  );

  // ── Derived values ──────────────────────────────────────────────────────────
  const months    = generateMonths(budget.startMonth, budget.endMonth);
  const resources = budget.resources || [];
  const activities = budget.activities || [];
  const licenses  = budget.licenses || [];

  const period = budget.startMonth && budget.endMonth
    ? `${fmtMonthShort(budget.startMonth)} – ${fmtMonthShort(budget.endMonth)}`
    : 'samtliga';

  const monthData = months.map(m => {
    const hrs = activities.reduce((s, act) =>
      s + (act.resourceLines || []).reduce((ls, l) => ls + (parseFloat(l.months?.[m]) || 0), 0), 0);
    const val = activities.reduce((s, act) =>
      s + (act.resourceLines || []).reduce((ls, l) => {
        const res = resources.find(r => r.id === l.resourceId);
        return ls + (parseFloat(l.months?.[m]) || 0) * (parseFloat(res?.hourlyRate) || 0);
      }, 0), 0);
    return { m, hrs, val };
  });

  const resourceData = resources.map(res => {
    const hrs = activities.reduce((s, act) =>
      s + (act.resourceLines || [])
        .filter(l => l.resourceId === res.id)
        .reduce((ls, l) => ls + months.reduce((ms, mo) => ms + (parseFloat(l.months?.[mo]) || 0), 0), 0), 0);
    return { res, hrs, val: hrs * (parseFloat(res.hourlyRate) || 0) };
  }).filter(r => r.hrs > 0);

  const activityData = activities.map(act => {
    const lines = act.resourceLines || [];
    const hrs = lines.reduce((s, l) => s + months.reduce((ms, m) => ms + (parseFloat(l.months?.[m]) || 0), 0), 0);
    const val = lines.reduce((s, l) => {
      const res = resources.find(r => r.id === l.resourceId);
      return s + months.reduce((ms, m) => ms + (parseFloat(l.months?.[m]) || 0), 0) * (parseFloat(res?.hourlyRate) || 0);
    }, 0);
    const rates = [...new Set(lines.filter(l => l.resourceId)
      .map(l => n(resources.find(r => r.id === l.resourceId)?.hourlyRate)).filter(r => r > 0))];
    return { act, hrs, val, apris: rates.length === 1 ? rates[0] : null };
  });

  const licenseData = licenses.map(lic => ({ lic, total: licTotal(lic) }));
  const totalArvoden  = resourceData.reduce((s, r) => s + r.val, 0);
  const totalLicenser = licenseData.reduce((s, l) => s + l.total, 0);
  const grandTotal    = totalArvoden + totalLicenser;

  // Consistent column widths — rightmost "SEK" col is always 18%
  // 3-col tables: 64 / 18 / 18
  // 4-col tables: 46 / 18 / 18 / 18
  // 5-col tables: 34 / 12 / 14 / 22 / 18

  // ── Report HTML ─────────────────────────────────────────────────────────────
  const Report = ({ printWidth } = {}) => (
    <div className="budget-report-content" style={{
      fontFamily: 'Arial, Helvetica, sans-serif',
      color: '#000',
      fontSize: basePt + 'pt',
    }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <table width="100%" cellSpacing={0} cellPadding={0}
        style={{ borderCollapse: 'collapse', marginBottom: 20 }}>
        <colgroup>
          <col style={{ width: '13%' }} />
          <col style={{ width: '28%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '11%' }} />
        </colgroup>
        <tbody>
          {/* Row 1: labels — Projektnamn spans cols 2-5, BUDGET in col 6 */}
          <tr>
            <td rowSpan={6} style={{ verticalAlign: 'top', paddingRight: 14, paddingTop: 2 }}>
              <img src={logoUrl} alt="Logo"
                style={{ maxWidth: 75, maxHeight: 62, objectFit: 'contain', display: 'block' }} />
            </td>
            <td colSpan={4} style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2 }}>
              Projektnamn
            </td>
            <td rowSpan={6} style={{ verticalAlign: 'top', textAlign: 'right', paddingLeft: 10, borderLeft: '1pt solid #ddd' }}>
              <div style={{ fontSize: '1.55em', fontWeight: 900, color: '#000', letterSpacing: '1pt', lineHeight: 1 }}>
                BUDGET
              </div>
            </td>
          </tr>
          {/* Row 2: project name value — large, spans cols 2-5 */}
          <tr>
            <td colSpan={4} style={{ fontSize: '1.1em', fontWeight: 700, paddingBottom: 7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {project.name || '–'}
            </td>
          </tr>
          {/* Row 3: labels */}
          <tr>
            <td style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2, paddingRight: 10 }}>Projektnummer</td>
            <td style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2, paddingRight: 10 }}>Budgetperiod</td>
            <td style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2, paddingRight: 8 }}>Datum</td>
            <td style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2 }}>Rev</td>
          </tr>
          {/* Row 4: values */}
          <tr>
            <td style={{ fontSize: '1em', paddingBottom: 7, paddingRight: 10 }}>{project.projectNumber || '–'}</td>
            <td style={{ fontSize: '1em', paddingBottom: 7, paddingRight: 10 }}>{period}</td>
            <td style={{ fontSize: '1em', paddingBottom: 7, paddingRight: 8 }}>{datum || '–'}</td>
            <td style={{ fontSize: '1em', paddingBottom: 7 }}>{revision || '–'}</td>
          </tr>
          {/* Row 5: labels */}
          <tr>
            <td style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2, paddingRight: 10 }}>{budgetnamn ? 'Budgetnamn' : 'Företag'}</td>
            <td style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2, paddingRight: 10 }}>{budgetnamn ? 'Företag' : 'Skapad av'}</td>
            <td colSpan={2} style={{ fontWeight: 700, fontSize: '0.78em', color: '#555', paddingBottom: 2 }}>{budgetnamn ? 'Skapad av' : ''}</td>
          </tr>
          {/* Row 6: values */}
          <tr>
            <td style={{ fontSize: '1em', paddingRight: 10 }}>{budgetnamn || foretag || '–'}</td>
            <td style={{ fontSize: '1em', paddingRight: 10 }}>{budgetnamn ? (foretag || '–') : (skapadAv || '–')}</td>
            <td colSpan={2} style={{ fontSize: '1em' }}>{budgetnamn ? (skapadAv || '–') : ''}</td>
          </tr>
        </tbody>
      </table>

      {/* ── Månader ─────────────────────────────────────────────────────── */}
      <SectionTable>
        <tr>
          <TH w="64%">Månader</TH>
          <TH align="right" w="18%">timmar</TH>
          <TH align="right" w="18%">SEK</TH>
        </tr>
        {monthData.map(({ m, hrs, val }) => (
          <tr key={m}>
            <TD>{fmtMonthLong(m)}</TD>
            <TD align="right">{hrs > 0 ? n(hrs) : '–'}</TD>
            <TD align="right">{val > 0 ? sep(val) : '–'}</TD>
          </tr>
        ))}
      </SectionTable>

      {/* ── Roller / Personer ────────────────────────────────────────────── */}
      {resourceData.length > 0 && (
        <SectionTable>
          <tr>
            <TH w="46%">Roller / Personer</TH>
            <TH align="right" w="18%">kr/tim</TH>
            <TH align="right" w="18%">timmar</TH>
            <TH align="right" w="18%">SEK</TH>
          </tr>
          {resourceData.map(({ res, hrs, val }) => (
            <tr key={res.id}>
              <TD>{res.name || '–'}</TD>
              <TD align="right">{n(res.hourlyRate) > 0 ? sep(res.hourlyRate) : '–'}</TD>
              <TD align="right">{n(hrs)}</TD>
              <TD align="right">{sep(val)}</TD>
            </tr>
          ))}
        </SectionTable>
      )}

      {/* ── Aktiviteter ──────────────────────────────────────────────────── */}
      {activityData.length > 0 && (
        <SectionTable>
          <tr>
            <TH w="34%">Aktiviteter</TH>
            <TH align="right" w="12%">Antal</TH>
            <TH w="14%">Enhet</TH>
            <TH align="right" w="22%">Á-pris</TH>
            <TH align="right" w="18%">SEK</TH>
          </tr>
          {activityData.map(({ act, hrs, val, apris }) => (
            <tr key={act.id}>
              <TD>{act.name || '–'}</TD>
              <TD align="right">{hrs > 0 ? n(hrs) : '–'}</TD>
              <TD>timme</TD>
              <TD align="right">{apris ? sep(apris) : '–'}</TD>
              <TD align="right">{val > 0 ? sep(val) : '–'}</TD>
            </tr>
          ))}
        </SectionTable>
      )}

      {/* ── Licenskostnader ─────────────────────────────────────────────── */}
      {licenseData.length > 0 && (
        <SectionTable>
          <tr>
            <TH w="34%">Licenskostnader</TH>
            <TH align="right" w="12%">Antal</TH>
            <TH w="14%">Pristyp</TH>
            <TH align="right" w="22%">Á-pris</TH>
            <TH align="right" w="18%">SEK</TH>
          </tr>
          {licenseData.map(({ lic, total }) => (
            <tr key={lic.id}>
              <TD>{lic.name || '–'}</TD>
              <TD align="right">{lic.priceType !== 'fixed' ? (parseFloat(lic.quantity) || 1) : '–'}</TD>
              <TD>{LIC_LABEL[lic.priceType] || lic.priceType}</TD>
              <TD align="right">{parseFloat(lic.price) > 0 ? sep(lic.price) : '–'}</TD>
              <TD align="right">{sep(total)}</TD>
            </tr>
          ))}
        </SectionTable>
      )}

      {/* ── Totalt ──────────────────────────────────────────────────────── */}
      <table width="100%" cellSpacing={0} cellPadding={0}
        style={{ borderCollapse: 'collapse', marginTop: 6 }}>
        <colgroup>
          <col style={{ width: '82%' }} />
          <col style={{ width: '18%' }} />
        </colgroup>
        <tbody>
          {totalLicenser > 0 && (
            <tr>
              <td style={{ fontSize: '0.86em', color: '#555', paddingBottom: 2, paddingLeft: 12 }}>
                Arvoden
              </td>
              <td style={{ fontSize: '0.86em', color: '#555', textAlign: 'right', paddingBottom: 2, paddingRight: 6 }}>
                {sep(totalArvoden)} kr
              </td>
            </tr>
          )}
          {totalLicenser > 0 && (
            <tr>
              <td style={{ fontSize: '0.86em', color: '#555', paddingBottom: 5, paddingLeft: 12 }}>
                Licenskostnader
              </td>
              <td style={{ fontSize: '0.86em', color: '#555', textAlign: 'right', paddingBottom: 5, paddingRight: 6 }}>
                {sep(totalLicenser)} kr
              </td>
            </tr>
          )}
          <tr>
            <td style={{
              background: '#0077B4', color: 'white',
              fontWeight: 700, fontSize: '1.1em',
              padding: '6pt 0 6pt 12pt',
              borderRadius: '2pt 0 0 2pt',
            }}>
              Totalt budget
            </td>
            <td style={{
              background: '#0077B4', color: 'white',
              fontWeight: 700, fontSize: '1.4em',
              padding: '6pt 6pt 6pt 6pt',
              textAlign: 'right', whiteSpace: 'nowrap',
              borderRadius: '0 2pt 2pt 0',
            }}>
              {sep(grandTotal)} kr
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Belastningsdiagram ──────────────────────────────────────────── */}
      {months.length > 1 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 700, fontSize: '0.86em', borderBottom: '0.75pt solid #000', paddingBottom: 3, marginBottom: 6 }}>
            Belastning över tid
          </div>
          <BudgetChart
            months={months}
            resources={resources}
            activities={activities}
            licenses={licenses}
            height={170}
            printWidth={printWidth}
          />
        </div>
      )}

    </div>
  );

  // ── Screen render ───────────────────────────────────────────────────────────
  const darkInput = {
    InputLabelProps: { sx: { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' } },
    inputProps: { style: { color: 'white', fontSize: '0.85rem' } },
    sx: {
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
    },
  };

  return (
    <Box className="budget-root" sx={{ bgcolor: '#e8e8e8', minHeight: '100vh' }}>

      {/* Toolbar */}
      <Box className="no-print" sx={{
        bgcolor: '#1a1a1a', color: 'white',
        display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, flexWrap: 'wrap',
      }}>
        <Button size="small" startIcon={<ArrowBackIcon />} variant="outlined"
          onClick={() => navigate(-1)}
          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', mr: 1 }}>
          Tillbaka
        </Button>

        <TextField label="Budgetnamn" size="small" value={budgetnamn}
          onChange={e => setBudgetnamn(e.target.value)}
          onBlur={e => saveBudgetMeta('budgetnamn', e.target.value)}
          placeholder="Ex. SH / PH / Förstudie"
          {...darkInput}
          sx={{ width: 200, ...darkInput.sx }} />

        <TextField label="Företag" size="small" value={foretag}
          onChange={e => setForetag(e.target.value)}
          onBlur={e => saveBudgetMeta('foretag', e.target.value)}
          {...darkInput}
          sx={{ width: 180, ...darkInput.sx }} />

        <TextField label="Skapad av" size="small" value={skapadAv}
          onChange={e => setSkapadAv(e.target.value)}
          onBlur={e => saveBudgetMeta('skapadAv', e.target.value)}
          {...darkInput}
          sx={{ width: 160, ...darkInput.sx }} />

        <TextField label="Datum" size="small" value={datum}
          onChange={e => setDatum(e.target.value)}
          onBlur={e => saveBudgetMeta('datum', e.target.value)}
          placeholder="ÅÅÅÅ-MM-DD"
          {...darkInput}
          sx={{ width: 130, ...darkInput.sx }} />

        <TextField label="Rev (A/B/C…)" size="small" value={revision}
          onChange={e => setRevision(e.target.value.toUpperCase())}
          onBlur={e => saveBudgetMeta('revision', e.target.value.toUpperCase())}
          {...darkInput}
          sx={{ width: 100, ...darkInput.sx }} />

        <Box sx={{ flex: 1 }} />

        {/* S / M / L size toggle */}
        <Box sx={{ display: 'flex', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 1, overflow: 'hidden' }}>
          {['S', 'M', 'L'].map(s => (
            <button key={s} onClick={() => setSizeMode(s)} style={{
              background: sizeMode === s ? '#0077B4' : 'transparent',
              color: 'white', border: 'none', padding: '4px 12px',
              cursor: 'pointer', fontWeight: sizeMode === s ? 700 : 400,
              fontSize: '0.8rem',
            }}>{s}</button>
          ))}
        </Box>

        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 700, px: 3 }}>
          Skriv ut / Spara PDF
        </Button>
      </Box>

      {/* A4 paper preview — screen only */}
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Box sx={{
          width: '210mm', minHeight: '297mm',
          bgcolor: 'white',
          boxShadow: '0 4px 24px rgba(0,0,0,0.22)',
          p: '15mm',
          boxSizing: 'border-box',
        }}>
          <Report />
        </Box>
      </Box>

      {/* Print-only content — no wrapper box, page margins handle spacing */}
      <div className="print-only">
        <Report printWidth={680} />
      </div>

      {/* Print CSS */}
      <style>{`
        .print-only { display: none; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          html, body, .budget-root { background: white !important; }
          body { margin: 0; padding: 0; }
          @page { size: A4 portrait; margin: 15mm; }
          .budget-report-content { font-size: ${basePt}pt !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </Box>
  );
}
